import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { FUEL_LOCATIONS } from '@/lib/fuelLocations';
import { toast, Toaster } from 'sonner';
import { Fuel, Zap, MapPin, Search, Navigation, Trophy, Clock, Plug, ArrowRight } from 'lucide-react';

const SITE = 'https://tradesmateuk.co.uk';
const FUEL_FAQ = [
  { q: 'How do I find the cheapest fuel near me?',
    a: 'Enter your postcode or tap "Use my location" and TradesMate lists petrol and diesel prices from major retailers within your chosen radius, with the cheapest forecourt highlighted at the top.' },
  { q: 'How often are UK fuel prices updated?',
    a: "Prices come from the UK government's open fuel-price scheme and are refreshed throughout the day, so they closely track what you'll pay at the pump." },
  { q: 'Which fuel stations are included?',
    a: 'Participating major retailers such as Asda, Tesco, Morrisons, Sainsbury\u2019s, Esso, BP, Shell and Motor Fuel Group. Small independents that don\u2019t publish price data won\u2019t appear.' },
  { q: 'Is the fuel price checker free?',
    a: 'Yes. Checking live petrol, diesel and EV charging prices is completely free with no sign-up needed. TradesMate is built for UK tradesmen but anyone can use the fuel tracker.' },
];
const FUEL_JSONLD = [
  { '@context': 'https://schema.org', '@type': 'WebApplication',
    name: 'TradesMate UK Fuel Price Tracker', applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Web', url: `${SITE}/fuel`,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
    description: 'Free live UK fuel-price tracker — find the cheapest petrol and diesel near you plus nearby EV charging points.' },
  { '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FUEL_FAQ.map((f) => ({ '@type': 'Question', name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
];

const FUELS = [
  { key: 'E10', label: 'Unleaded' },
  { key: 'B7', label: 'Diesel' },
  { key: 'E5', label: 'Super Unleaded' },
  { key: 'SDV', label: 'Super Diesel' },
];

const fmtP = (p) => (p == null ? '—' : `${Number(p).toFixed(1)}p`);
const timeAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return h < 24 ? `${h} hr ago` : `${Math.round(h / 24)} d ago`;
};

export default function FuelPage() {
  const [mode, setMode] = useState('fuel');
  const [postcode, setPostcode] = useState('');
  const [radius, setRadius] = useState(5);
  const [fuel, setFuel] = useState('E10');
  const [loc, setLoc] = useState(null); // {lat,lng,label}
  const [loading, setLoading] = useState(false);
  const [fuelData, setFuelData] = useState(null);
  const [evData, setEvData] = useState(null);

  const runSearch = async (coords, label, m = mode, f = fuel, r = radius) => {
    setLoading(true);
    setLoc({ ...coords, label });
    try {
      if (m === 'fuel') {
        const res = await api.get('/fuel/stations', { params: { lat: coords.lat, lng: coords.lng, radius: r, fuel: f } });
        setFuelData(res.data);
      } else {
        const res = await api.get('/ev/chargers', { params: { lat: coords.lat, lng: coords.lng, radius: r } });
        setEvData(res.data);
      }
    } catch (e) {
      const msg = e.response?.status === 503 ? 'EV charger map is coming soon.' : (e.response?.data?.detail || 'Something went wrong');
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong');
      if (m === 'fuel') setFuelData(null); else setEvData(null);
    } finally { setLoading(false); }
  };

  const onPostcode = async (e) => {
    e?.preventDefault();
    if (!postcode.trim()) { toast.error('Enter a postcode'); return; }
    setLoading(true);
    try {
      const g = await api.get('/geo/postcode', { params: { q: postcode } });
      await runSearch({ lat: g.data.lat, lng: g.data.lng }, g.data.postcode);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Postcode not found');
      setLoading(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Location not supported on this device'); return; }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => runSearch({ lat: pos.coords.latitude, lng: pos.coords.longitude }, 'Your location'),
      () => { toast.error('Could not get your location — try a postcode'); setLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const reSearch = (opts) => { if (loc) runSearch({ lat: loc.lat, lng: loc.lng }, loc.label, opts.m ?? mode, opts.f ?? fuel, opts.r ?? radius); };
  const switchMode = (m) => { setMode(m); if (loc) reSearch({ m }); };
  const setFuelAnd = (f) => { setFuel(f); if (loc) reSearch({ f }); };

  const stations = fuelData?.stations || [];
  const cheapest = stations.find((s) => s[fuel] != null);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Cheapest Fuel Near You — Live UK Petrol & Diesel Prices + EV Chargers"
        description="Find the cheapest petrol and diesel near you with live UK fuel prices from major retailers, updated throughout the day. Plus nearby EV charging points — free, no sign-up."
        path="/fuel" kicker="Live UK Fuel Prices" jsonLd={FUEL_JSONLD} />
      <Toaster position="top-center" richColors />

      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Live · UK-wide · free</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Fuel Near You</h1>
        <p className="text-slate-600 mt-2">Find the cheapest petrol &amp; diesel near you — plus nearby EV charging points. Prices refresh throughout the day from major UK retailers.</p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-5">
        <button onClick={() => switchMode('fuel')} data-testid="mode-fuel"
          className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wide border ${mode === 'fuel' ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300'}`}>
          <Fuel size={16}/> Petrol &amp; Diesel
        </button>
        <button onClick={() => switchMode('ev')} data-testid="mode-ev"
          className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wide border ${mode === 'ev' ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300'}`}>
          <Zap size={16}/> EV Chargers
        </button>
      </div>

      {/* Search controls */}
      <div className="tm-card p-5 mb-6">
        <form onSubmit={onPostcode} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="tm-input pl-9" placeholder="Enter postcode (e.g. M1 1AE)" value={postcode}
                onChange={(e) => setPostcode(e.target.value)} data-testid="fuel-postcode-input" />
            </div>
            <button type="submit" className="tm-btn" data-testid="fuel-search-btn"><Search size={16}/> Search</button>
          </div>
          <button type="button" onClick={useMyLocation} className="tm-btn tm-btn-ghost" data-testid="fuel-my-location-btn">
            <Navigation size={16}/> Use my location
          </button>
        </form>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <label className="flex items-center gap-3 flex-1">
            <span className="text-xs uppercase tracking-widest text-slate-500 whitespace-nowrap font-semibold">Within {radius} mi</span>
            <input type="range" min="1" max="20" value={radius} onChange={(e) => setRadius(Number(e.target.value))}
              onMouseUp={() => reSearch({})} onTouchEnd={() => reSearch({})}
              className="flex-1 accent-brand" data-testid="fuel-radius-slider" />
          </label>
          {mode === 'fuel' && (
            <div className="flex flex-wrap gap-1">
              {FUELS.map((ft) => (
                <button key={ft.key} onClick={() => setFuelAnd(ft.key)} data-testid={`fuel-type-${ft.key}`}
                  className={`px-2.5 py-1 text-xs font-mono uppercase border ${fuel === ft.key ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-300'}`}>
                  {ft.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && <p className="font-mono text-slate-400" data-testid="fuel-loading">Searching…</p>}

      {/* FUEL RESULTS */}
      {!loading && mode === 'fuel' && fuelData && (
        <div data-testid="fuel-results">
          {stations.length === 0 ? (
            <div className="tm-card p-8 text-center text-slate-500">No stations found within {radius} miles. Try a wider radius.</div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3 text-xs font-mono text-slate-500">
                <span>{fuelData.total_in_radius} stations within {radius} mi of {loc?.label}</span>
                {fuelData.updated_at && <span className="flex items-center gap-1"><Clock size={12}/> Updated {timeAgo(fuelData.updated_at)}</span>}
              </div>
              <div className="space-y-2">
                {stations.map((s, i) => {
                  const best = cheapest && s.site_id === cheapest.site_id;
                  return (
                    <div key={s.site_id} data-testid={`fuel-station-${i}`}
                      className={`tm-card p-4 flex items-center gap-4 ${best ? 'ring-2 ring-emerald-500' : ''}`}>
                      <div className="text-center shrink-0 w-24">
                        <div className={`font-display font-semibold text-2xl ${best ? 'text-emerald-600' : 'text-slate-900'}`}>{fmtP(s[fuel])}</div>
                        {best && <span className="inline-flex items-center gap-1 text-[10px] uppercase text-emerald-600 font-semibold"><Trophy size={11}/> Cheapest</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold">{s.brand}</p>
                        <p className="text-sm text-slate-500 truncate">{s.address}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] font-mono text-slate-400">
                          {FUELS.map((ft) => s[ft.key] != null && <span key={ft.key}>{ft.label.split(' ')[0]}: {fmtP(s[ft.key])}</span>)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-sm font-bold text-slate-700">{s.distance} mi</div>
                        <a href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`} target="_blank" rel="noreferrer"
                          className="text-[11px] font-mono text-brand hover:underline">Directions →</a>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400 mt-4 leading-snug">
                Prices from participating major retailers ({(fuelData.brands || []).join(', ')}) via the UK government's open fuel-price scheme. Small independents that don't publish data won't appear. Always check at the pump.
              </p>
            </>
          )}
        </div>
      )}

      {/* EV RESULTS */}
      {!loading && mode === 'ev' && evData && (
        <div data-testid="ev-results">
          {(evData.chargers || []).length === 0 ? (
            <div className="tm-card p-8 text-center text-slate-500">No charging points found within {radius} miles. Try a wider radius.</div>
          ) : (
            <>
              <p className="text-xs font-mono text-slate-500 mb-3">{evData.count} charging locations within {radius} mi of {loc?.label}</p>
              <div className="space-y-2">
                {evData.chargers.map((c, i) => (
                  <div key={i} data-testid={`ev-charger-${i}`} className="tm-card p-4 flex items-center gap-4">
                    <div className="w-12 h-12 shrink-0 bg-emerald-50 border border-emerald-200 rounded flex items-center justify-center">
                      <Plug size={20} className="text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate">{c.title || 'Charging point'}</p>
                      <p className="text-sm text-slate-500 truncate">{c.address}</p>
                      <div className="flex flex-wrap gap-x-3 mt-1 text-[11px] font-mono text-slate-400">
                        {c.network && <span>{c.network}</span>}
                        {c.connections > 0 && <span>{c.connections} connector{c.connections === 1 ? '' : 's'}</span>}
                        {c.max_kw > 0 && <span>up to {c.max_kw}kW</span>}
                        {(c.types || []).slice(0, 2).map((t) => <span key={t}>{t}</span>)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-sm font-bold text-slate-700">{c.distance} mi</div>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`} target="_blank" rel="noreferrer"
                        className="text-[11px] font-mono text-brand hover:underline">Directions →</a>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {!loc && !loading && (
        <div className="tm-card p-10 text-center" data-testid="fuel-empty">
          <Fuel size={40} className="mx-auto text-slate-300 mb-3" />
          <h3 className="font-display font-bold text-lg uppercase">Find the cheapest fuel near you</h3>
          <p className="text-slate-500 mt-1">Enter a postcode or tap "Use my location" to get started.</p>
        </div>
      )}

      {/* Popular locations — internal links for programmatic SEO */}
      <section className="mt-14" data-testid="fuel-popular-locations">
        <h2 className="font-display font-semibold uppercase tracking-tight text-2xl mb-1">Cheapest fuel by city</h2>
        <p className="text-slate-600 mb-4">Jump straight to live petrol &amp; diesel prices in a major UK city.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {FUEL_LOCATIONS.map((l) => (
            <Link key={l.slug} to={`/fuel/${l.slug}`} data-testid={`fuel-city-${l.slug}`}
              className="tm-card p-3 hover:shadow-lg transition-shadow flex items-center justify-between">
              <span className="font-medium text-sm">{l.name}</span>
              <ArrowRight size={14} className="text-brand" />
            </Link>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="mt-12">
        <h2 className="font-display font-semibold uppercase tracking-tight text-2xl mb-3">How the UK fuel price tracker works</h2>
        <p className="text-slate-700 leading-relaxed mb-2">
          Fuel is one of the biggest running costs for any UK tradesman — often thousands of pounds a year across a
          busy work van. Prices between forecourts can differ by 10p a litre or more, so a quick compare before you
          fill saves real money over a year.
        </p>
        <ul className="list-disc pl-6 space-y-1 text-slate-700 mt-2">
          <li>We pull live prices from the UK government's open fuel-price scheme, updated throughout the day.</li>
          <li>Search by postcode or GPS and sort forecourts by price and distance.</li>
          <li>Supermarket forecourts are usually cheapest — fill up before joining the motorway.</li>
          <li>Diesel and unleaded don't always move together, so switch fuel type to compare yours.</li>
        </ul>
      </section>

      {/* FAQ */}
      <section className="mt-10">
        <h2 className="font-display font-bold text-xl uppercase mb-4">Fuel prices — FAQs</h2>
        <div className="space-y-4">
          {FUEL_FAQ.map((f, i) => (
            <div key={i} className="tm-card p-4" data-testid={`fuel-faq-${i}`}>
              <h3 className="font-bold text-base">{f.q}</h3>
              <p className="text-slate-600 text-sm mt-1">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
